import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';

// Components
import { LoadingOutlined } from '@ant-design/icons';
import { GoogleMap } from 'components/bos-modules';
import LocationCard from 'components/location-card/LocationCard';
import LandingPageAddressInput from 'top-component-library/Fields/LandingPageAddressInput';

// Models
import Location from 'models/Location';

// Constants
import { TOPLoggedError } from 'constants/Errors';
import { MAP_TEMPLATE, MAP_TEMPLATE_TOGGLE_OPTIONS } from 'constants/FulfillmentMethods';

// Hooks
import { useStores } from 'hooks/use-stores';

// Utils
import { getCookie, setCookie } from 'utils/Cookie';

// Tracking
import { bosLandingPageTrackingEvents } from 'integrations/segment/tracking-events';

import {
  MapAddressInputErrorMessage,
  MapContainer,
  MapCustomHeader,
  MapFulfillmentMethodForm,
  MapFulfillmentMethodToggle,
  MapInputContainer,
  MapLabel,
  MapLoadingSpinner,
  MapLoadingSpinnerContainer,
  MapLocationPinIcon,
  MapLocationsCount,
  MapLocationsList,
  MapMapColumn,
  MapPage,
  MapScrollableContainer,
  MapSearchColumn,
  MapSearchColumnTop,
  MapTitle,
  MapVendorSelect,
} from './styles';

export const Map = observer(() => {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [vendorsById, setVendorsById] = useState({});
  const [locationsCustomersById, setLocationsCustomersById] = useState(null);
  const [showFulfillmentMethodToggle, setShowFulfillmentMethodToggle] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [addressErrorMessage, setAddressErrorMessage] = useState(null);
  const [locationFilter, setLocationFilter] = useState({
    selectedFulfillmentMethodToggleValue: MAP_TEMPLATE_TOGGLE_OPTIONS.PICKUP,
    locationsInRange: null,
    vendors: new Set(),
  });

  const { hostStore, uiState, GoogleMapsAPI } = useStores();
  const { landingPageLocations, customersById, settings } = hostStore;
  const { isMobile } = uiState;

  const options = [
    { label: 'Pickup', value: MAP_TEMPLATE_TOGGLE_OPTIONS.PICKUP, index: 0 },
    { label: 'Delivery', value: MAP_TEMPLATE_TOGGLE_OPTIONS.DELIVERY, index: 1 },
  ];

  // helper function used to sort the locations by distance
  const compareLocationsByCustomerDistance = useCallback(
    (loc1, loc2) => {
      if (
        locationsCustomersById[loc1.customer]?.distanceInMiles < locationsCustomersById[loc2.customer]?.distanceInMiles
      ) {
        return -1;
      }
      if (
        locationsCustomersById[loc1.customer]?.distanceInMiles > locationsCustomersById[loc2.customer]?.distanceInMiles
      ) {
        return 1;
      }
      return 0;
    },
    [locationsCustomersById]
  );

  /** Sets the list of location codes that are allowed to be shown on the map template. ["patron_choice",
   * "patron_pickup", "driver_delivery"] location codes.
   */
  useEffect(() => {
    setLocations(
      landingPageLocations
        .filter((loc) => {
          const supported = Location.supportedOnMapTemplate(loc);

          if (!supported) {
            new TOPLoggedError(
              `Misconfiguration: the map template does not support fulfillment method(s) ${
                loc.fulfillment_method !== 'patron_choice'
                  ? loc.fulfillment_method
                  : loc.possible_fulfillment_methods.map((option) => option.fulfillment_method).join(', ')
              }, location code ${loc.locationShortId} for customer ${
                customersById[loc.customer]?.customer_name
              } was not displayed in the list of location codes.`
            );
          }

          return supported;
        })
        .map((loc) => new Location(loc, customersById))
    );
  }, [landingPageLocations, customersById]);

  /** Set the CustomersById for customers that actually have location codes that render on the map template. This sets
   * detailedCustomerById to be a dict of customers with ["patron_choice", "patron_pickup", "driver_delivery"] location
   * codes.
   */
  useEffect(() => {
    if (landingPageLocations && customersById) {
      const locationCustomerDict = {};
      landingPageLocations.forEach((loc) => {
        locationCustomerDict[loc.customer] = customersById[loc.customer];
      });
      setLocationsCustomersById(locationCustomerDict);
    }
  }, [landingPageLocations, customersById]);

  /** Sets the available selected fulfillment methods, used to show or not show the toggle. */
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (locations.length === 0) {
      return null;
    }
    // Get the unique set of fulfillment methods used by location codes
    const uniqueMethods = new Set(
      locations
        .map((loc) => Location.getSupportedFulfillmentMethods(loc, MAP_TEMPLATE.SUPPORTED_FULFILLMENT_METHODS)) // NOTE: returns a multi dimensional array [<array of fulfillment_methods keys>, <array of fulfillment_methods keys>]
        .reduce((acc, val) => acc.concat(val), []) // Flatten the multi dimensional array
    );

    const showToggle =
      uniqueMethods.size > 2 ||
      (uniqueMethods.size === 2 &&
        !(uniqueMethods.has('patron_pickup') && uniqueMethods.has('patron_pickup_with_expo')) &&
        !(uniqueMethods.has('driver_delivery') && uniqueMethods.has('driver_delivery_with_expo')));
    setShowFulfillmentMethodToggle(showToggle);

    let initialFulfillmentMethodToggleValue = getCookie('previously_selected_fulfillment_method');

    // If the guests has not previously selected one or the other toggle options
    if (
      initialFulfillmentMethodToggleValue !== MAP_TEMPLATE_TOGGLE_OPTIONS.PICKUP &&
      initialFulfillmentMethodToggleValue !== MAP_TEMPLATE_TOGGLE_OPTIONS.DELIVERY
    ) {
      // Check if what is saved in to the cart is already a valid option
      if (MAP_TEMPLATE.SUPPORTED_FULFILLMENT_METHODS.includes(initialFulfillmentMethodToggleValue)) {
        if (MAP_TEMPLATE.PICKUP_FULFILLMENT_METHODS.includes(initialFulfillmentMethodToggleValue)) {
          initialFulfillmentMethodToggleValue = MAP_TEMPLATE_TOGGLE_OPTIONS.PICKUP;
        } else {
          initialFulfillmentMethodToggleValue = MAP_TEMPLATE_TOGGLE_OPTIONS.DELIVERY;
        }
      } else if (MAP_TEMPLATE.PICKUP_FULFILLMENT_METHODS.filter((method) => uniqueMethods.has(method))?.length > 0) {
        initialFulfillmentMethodToggleValue = MAP_TEMPLATE_TOGGLE_OPTIONS.PICKUP;
      } else {
        initialFulfillmentMethodToggleValue = MAP_TEMPLATE_TOGGLE_OPTIONS.DELIVERY;
      }
    }

    setCookie('previously_selected_fulfillment_method', initialFulfillmentMethodToggleValue);
    setLocationFilter((l) => ({
      ...l,
      selectedFulfillmentMethodToggleValue: initialFulfillmentMethodToggleValue,
    }));
  }, [locations]);

  /** Sets the vendorsById dict that is used for filtering the location cards.
   */
  useEffect(() => {
    if (customersById) {
      const newVendorsById = {};
      locations?.forEach((location) => {
        location.availableVendors?.forEach((vendor) => {
          if (vendor?.brand_parent) {
            // If part of a brand then list the vendor but the customer_name and logo_url will be the chain parent's
            // Use the brand parent customer id as the key.
            newVendorsById[vendor.brand_parent.customer_id] = vendor.brand_parent;
          } else if (vendor) {
            // If not part of a brand still list the vendor as an option
            newVendorsById[vendor.customer_id] = vendor;
          }
        });
      });
      if (Object.keys(newVendorsById).length > 0) {
        setVendorsById(newVendorsById);
      }
    }
  }, [locations, customersById]);

  /** Updates the list of location codes that fit the criteria set by the different filters: VendorById filter,
   * canDeliver filter, and fulfillment type filter.
   */
  useEffect(() => {
    const { selectedFulfillmentMethodToggleValue, vendors } = locationFilter;
    let filteredLocationList = locations;

    // If there are vendors to filter by
    if (vendors.size) {
      filteredLocationList = filteredLocationList.filter((location) => {
        // Check the vendors for that location and see if it has all specified vendors
        const matchingVendors = location.availableVendors?.filter(
          (vendor) => vendors.has(vendor.customer_id) || vendors.has(vendor.brand_parent?.customer_id)
        );
        return matchingVendors.length - vendors.size === 0;
      });
    }

    // If there are fulfillment methods to filter by
    if (selectedFulfillmentMethodToggleValue === MAP_TEMPLATE_TOGGLE_OPTIONS.PICKUP) {
      filteredLocationList = filteredLocationList.filter((loc) => Location.supportsPickup(loc));
    } else if (selectedFulfillmentMethodToggleValue === MAP_TEMPLATE_TOGGLE_OPTIONS.DELIVERY) {
      // Note: can deliver is appended to the customer when that address is inputted
      filteredLocationList = filteredLocationList.filter(
        (loc) => Location.supportsDelivery(loc) && locationsCustomersById[loc.customer]?.canDeliver
      );
    }

    setFilteredLocations(filteredLocationList.sort(compareLocationsByCustomerDistance));
  }, [locations, locationFilter, customersById, locationsCustomersById, compareLocationsByCustomerDistance]);

  // For all the customers that are out of range but still have location cards, append the haversine distance so that
  // you can show an estimated distance from the current location inputted.
  const onAddressObjectChange = async (addressObject) => {
    try {
      const customerPromises = Object.values(locationsCustomersById).map(
        async (customer) => await GoogleMapsAPI.checkAddressIsWithinRangeOfCustomer(customer, addressObject)
      );
      const customersThatCanDeliver = await Promise.all(customerPromises);
      setAddressErrorMessage(null);
      const updatedCustomersById = {};
      customersThatCanDeliver.forEach((customer) => {
        updatedCustomersById[customer.customer_id] = customer;
      });
      setCurrentLocation(addressObject);
      setLocationsCustomersById(updatedCustomersById);
      setLocationFilter({ ...locationFilter, locationsInRange: locations });
    } catch (error) {
      if (!(error instanceof TOPLoggedError)) {
        console.error(error);
      }
      setAddressErrorMessage(error?.message);
    }
  };

  const onFulfillmentMethodChange = (val) => {
    bosLandingPageTrackingEvents.trackClickToggleFulfillmentTypeStorePicker({ fulfillment_method: val });
    setCookie('previously_selected_fulfillment_method', val.fulfillment_method);
    setLocationFilter({ ...locationFilter, selectedFulfillmentMethodToggleValue: val.fulfillment_method });
  };

  return (
    <MapPage HeaderComponent={<MapCustomHeader />} title="Home">
      <MapContainer>
        <MapScrollableContainer>
          <MapSearchColumn>
            <MapSearchColumnTop>
              {/* Title */}
              {settings.host_customer?.customer_name && (
                <MapTitle reduceMargin={showFulfillmentMethodToggle ? 0 : 1} titleText="Find your nearest location." />
              )}
              {/* Fulfillment Method */}
              {showFulfillmentMethodToggle && (
                <MapFulfillmentMethodForm onValuesChange={onFulfillmentMethodChange}>
                  <MapFulfillmentMethodToggle
                    choices={options}
                    id="patron-choice-select"
                    initialValue={locationFilter?.selectedFulfillmentMethodToggleValue}
                    label=""
                    name="fulfillment_method"
                    required={false}
                  />
                </MapFulfillmentMethodForm>
              )}
              {/* Address Input */}
              <MapInputContainer>
                <MapLabel htmlFor="address">Find Nearest Location</MapLabel>
                <LandingPageAddressInput
                  onAddressObjectChange={onAddressObjectChange}
                  onLoading={(loading) => setIsLoading(loading)}
                  customersById={locationsCustomersById}
                  onLocationsChange={(locationsInRange) => setLocationFilter({ ...locationFilter, locationsInRange })}
                  placeholder="Enter Your Address"
                />
                {addressErrorMessage && (
                  <MapAddressInputErrorMessage>{addressErrorMessage}</MapAddressInputErrorMessage>
                )}
              </MapInputContainer>
              {/* Vendor Select */}
              {/* TODO: Remove [window.location.hostname !== 'app.alontihub.com'] when we make showing the vendors configurable  */}
              {vendorsById && window.location.hostname !== 'app.alontihub.com' && (
                <MapInputContainer>
                  <MapLabel>Filter by Restaurant</MapLabel>
                  <MapVendorSelect
                    allowClear
                    maxTagCount="responsive"
                    mode="multiple"
                    onChange={(vendorIds) => setLocationFilter({ ...locationFilter, vendors: new Set(vendorIds) })}
                    options={Object.values(vendorsById).map(({ customer_id, customer_name, logo_url }) => ({
                      imagePath: logo_url,
                      label: customer_name,
                      value: customer_id,
                    }))}
                    placeholder="Choose a Restaurant"
                  />
                </MapInputContainer>
              )}
            </MapSearchColumnTop>
            <MapLocationsList>
              {filteredLocations.length > 0 ? (
                // Locations
                <>
                  {!isMobile && <MapLocationsCount>Locations ({filteredLocations.length})</MapLocationsCount>}
                  {filteredLocations.map((location, i) => (
                    <LocationCard
                      key={`location-card-${i}`}
                      location={location}
                      customer={locationsCustomersById[location.customer_id]}
                      vendors={location.availableVendors}
                    />
                  ))}
                </>
              ) : (
                // Spinner / Placeholder Content
                <MapLoadingSpinnerContainer>
                  {isLoading && <MapLoadingSpinner indicator={<LoadingOutlined spin />} size="large" />}
                  {currentLocation === null && !isLoading && (
                    <>
                      <MapLocationPinIcon />
                      <p>Enter your address to find the nearest location.</p>
                    </>
                  )}
                  {!isLoading && currentLocation !== null && <p>No Nearby Locations</p>}
                </MapLoadingSpinnerContainer>
              )}
            </MapLocationsList>
          </MapSearchColumn>
        </MapScrollableContainer>
        {/* Map */}
        {!isMobile && locationsCustomersById && (
          <MapMapColumn>
            <GoogleMap currentLocation={currentLocation} customersById={locationsCustomersById} locations={locations} />
          </MapMapColumn>
        )}
      </MapContainer>
    </MapPage>
  );
});

export default Map;
